import { useTranslation } from 'react-i18next';

export const ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  MANAGER: 'manager',
  TECHNICIAN: 'technician',
  SUPERADMIN: 'superadmin',
};

export const ROLE_HIERARCHY = [
  ROLES.USER,
  ROLES.MANAGER,
  ROLES.TECHNICIAN,
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
];

export const isValidRole = (role) => Object.values(ROLES).includes(role);

/**
 * Intelligent role checker with hierarchy support
 * @param {string} userRole - The user's current role
 * @param {string} requiredRole - The required role (supports modifiers)
 * @example
 * hasRole('admin', 'admin') // true - exact match
 * hasRole('superadmin', 'admin+') // true - superadmin is higher than admin
 * hasRole('user', 'admin+') // false - user is lower than admin
 * hasRole('manager', 'technician-') // true - manager is lower than technician
 *
 * @returns {boolean}
 */
export const hasRole = (userRole, requiredRole) => {
  if (!isValidRole(userRole)) {
    return false;
  }

  const modifier = requiredRole.slice(-1);
  const baseRole = modifier === '+' || modifier === '-'
    ? requiredRole.slice(0, -1)
    : requiredRole;

  if (!isValidRole(baseRole)) {
    return false;
  }

  const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const requiredRoleIndex = ROLE_HIERARCHY.indexOf(baseRole);

  if (!['+', '-'].includes(modifier)) {
    return userRole === requiredRole;
  }

  if (modifier === '+') {
    return userRoleIndex >= requiredRoleIndex;
  }

  if (modifier === '-') {
    return userRoleIndex <= requiredRoleIndex;
  }

  return false;
};

/**
 * Get human-readable role label
 * @param {string} role - User role
 * @returns {string}
 */
export const getRoleLabel = (role) => {
  const labels = {
    [ROLES.USER]: 'Utilisateur',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.ADMIN]: 'Admin',
    [ROLES.TECHNICIAN]: 'Technicien',
    [ROLES.SUPERADMIN]: 'Super Admin',
  };
  return labels[role] || role;
};

/**
 * Emails authorized to manage superadmin users
 */
export const SUPERADMIN_MANAGERS = [
  'jgrelet@globe-groupe.com',
  'gpotier@globe-groupe.com',
];

/** Localized role label; literal switch for the i18n guard. Unknown role -> "User" (audit callers check `isValidRole`). @returns {string} */
export const roleLabel = (t, role) => {
  switch (role) {
    case ROLES.SUPERADMIN:
      return t('Super Admin');
    case ROLES.ADMIN:
      return t('Admin');
    case ROLES.MANAGER:
      return t('Manager');
    case ROLES.TECHNICIAN:
      return t('Technician');
    default:
      return t('User');
  }
};

/** Localized access-mode label; literal ternary for the i18n guard. @returns {string} */
export const modeLabel = (t, mode) => (mode === 'rw' ? t('Read / Write') : mode === 'r' ? t('Read only') : mode);

/**
 * Hook returning a resolver that translates a role id into a localized label.
 * @returns {(role: string) => string}
 */
export const useRoleLabel = () => {
  const { t } = useTranslation();

  return (role) => roleLabel(t, role);
};
